import { useCallback } from 'react';

import Konva from 'konva';
import { useShallow } from 'zustand/react/shallow';

import { useMagicPenStore } from '@src/stores';

import {
  createTextInput,
  createTextEditorWrapper,
  createResizeHandles,
  setupDraggableBehavior,
  setupColorTracking,
  constrainInitialPosition,
  generateUniqueId,
  toRelativeDimensions,
  toRelativePosition,
  registerTextEditor,
  unregisterTextEditor,
} from '../../helpers';
import { isSafari } from '@src/utils';

import { STICKER_PLACEMENT_OFFSET, STICKER_FONT_SIZE } from '../../magicPen.constants';

import { EMagicPenShape, ICanvasObject, ICreatedBy } from '@src/types';
import { calculateResponsiveFontSize } from '../../helpers/bounds.helpers';

const useTextTool = (
  setIsEditingText: (editing: boolean) => void,
  createdBy: ICreatedBy,
  handleAddObjects: (object: ICanvasObject[]) => Promise<void>,
  emitAddAnnotation: (objects: ICanvasObject[]) => Promise<void>,
  emitUpdateAnnotation: (objects: ICanvasObject[]) => Promise<void>
) => {
  const { brushColor, activeSticker, activeTool, setObjects, addObjects } = useMagicPenStore(
    useShallow((state) => ({
      brushColor: state.brushColor,
      activeSticker: state.activeSticker,
      activeTool: state.activeTool,
      setObjects: state.setObjects,
      addObjects: state.addObjects,
    }))
  );

  const createTextEditor = useCallback(
    (textNode: Konva.Text, id: string, initialText: string = '') => {
      const stage = textNode.getStage();
      if (!stage) return;

      setIsEditingText(true);

      // Hide original text node
      textNode.hide();
      textNode.getLayer()?.batchDraw();

      // Calculate text position
      const textPosition = textNode.absolutePosition();
      const stageBox = stage.container().getBoundingClientRect();
      const areaPosition = {
        x: stageBox.left + textPosition.x,
        y: stageBox.top + textPosition.y,
      };

      const getStageRect = () => stage.container().getBoundingClientRect();

      // Create editor components
      const fillValue = textNode.fill();
      const originalColor = (typeof fillValue === 'string' ? fillValue : null) || brushColor;
      const stageContainer = stage.container() as HTMLDivElement;
      const textEditorWrapper = createTextEditorWrapper(textNode, areaPosition, stageContainer);
      const textEditorInput = createTextInput(textNode, initialText, originalColor);
      const resizeHandles = createResizeHandles(textEditorWrapper, stageContainer);

      registerTextEditor(id, textEditorWrapper);

      // Append input to wrapper
      textEditorWrapper.appendChild(textEditorInput);

      // Setup behaviors
      setupDraggableBehavior(textEditorWrapper, textEditorInput, resizeHandles, stageContainer);
      const { unsubscribe, textColorTracker } = setupColorTracking(textEditorInput);

      // Configure input sizing behaviour
      // Minimum width should fit a single character (plus padding), starting width is set by wrapper (~15 chars)
      const singleCharWidth = (() => {
        try {
          const metrics = textNode.measureSize ? textNode.measureSize('W') : null;
          return metrics?.width || textNode.fontSize() * 0.6;
        } catch {
          return textNode.fontSize() * 0.6;
        }
      })();
      const nodePadding = textNode.padding ? textNode.padding() : 0;
      const MIN_EDITOR_WIDTH = Math.max(1, singleCharWidth + nodePadding * 2);
      const MIN_EDITOR_HEIGHT = Math.max(32, textNode.fontSize() + 12);

      let isAtBottomLimit = false;

      const parsePx = (value: string) => {
        const parsed = Number.parseFloat(value);
        return Number.isFinite(parsed) ? parsed : 0;
      };

      const handleInput = () => {
        adjustEditorDimensions();
      };

      const adjustEditorDimensions = () => {
        requestAnimationFrame(() => {
          const stageRect = getStageRect();
          const currentLeft = parsePx(textEditorWrapper.style.left);
          const currentTop = parsePx(textEditorWrapper.style.top);
          const currentWidth = parsePx(textEditorWrapper.style.width);

          let targetWidth = Math.max(MIN_EDITOR_WIDTH, currentWidth);
          let clampedLeft = currentLeft;
          if (stageRect) {
            clampedLeft = Math.max(stageRect.left, Math.min(currentLeft, stageRect.right - targetWidth));
            const availableWidth = Math.max(1, stageRect.right - clampedLeft);
            targetWidth = Math.min(targetWidth, availableWidth);
          }
          textEditorWrapper.style.width = `${targetWidth}px`;
          textEditorInput.style.width = '100%';
          textEditorWrapper.style.left = `${clampedLeft}px`;

          const contentHeight = textEditorInput.scrollHeight;
          let targetHeight = Math.max(MIN_EDITOR_HEIGHT, contentHeight);

          let clampedTop = currentTop;
          if (stageRect) {
            const maxTopForHeight = stageRect.bottom - targetHeight;
            clampedTop = Math.min(currentTop, maxTopForHeight);
            clampedTop = Math.max(stageRect.top, clampedTop);

            const availableHeight = Math.max(1, stageRect.bottom - clampedTop);

            const previousAtBottomLimit = isAtBottomLimit;
            isAtBottomLimit = targetHeight >= availableHeight;
            if (isAtBottomLimit) {
              targetHeight = availableHeight;
            }
            textEditorWrapper.style.maxHeight = `${availableHeight}px`;
            if (isAtBottomLimit && !previousAtBottomLimit) {
              textEditorInput.style.caretColor = 'red';
            } else if (!isAtBottomLimit && previousAtBottomLimit) {
              textEditorInput.style.caretColor = '#000';
            }
          }

          const finalHeight = Math.max(1, targetHeight);

          textEditorWrapper.style.top = `${clampedTop}px`;
          textEditorWrapper.style.height = `${finalHeight}px`;
          textEditorInput.style.height = `${finalHeight}px`;
        });
      };

      // Focus and select text
      textEditorInput.focus();
      textEditorInput.scrollTop = 0;

      if (initialText && initialText.trim()) {
        textEditorInput.select();
      } else {
        textEditorInput.setSelectionRange(0, 0);
      }

      adjustEditorDimensions();

      // Cleanup function to save and close the text editor
      const removeTextarea = async () => {
        const finalText = textEditorInput.value.replace(/\n+$/, '');

        textEditorInput.value = finalText;

        const currentWrapperWidth = textEditorWrapper.offsetWidth;
        textEditorInput.style.width = '100%';
        textEditorWrapper.style.width = `${currentWrapperWidth}px`;

        textEditorInput.style.height = 'auto';
        void textEditorInput.offsetHeight;
        const naturalContentHeight = Math.max(MIN_EDITOR_HEIGHT, textEditorInput.scrollHeight);

        const stageRectAtSave = getStageRect();
        const wrapperTop = textEditorWrapper.offsetTop;
        const availableHeightAtSave = stageRectAtSave
          ? Math.max(1, stageRectAtSave.bottom - wrapperTop)
          : naturalContentHeight;
        const trimmedHeight = Math.min(naturalContentHeight, availableHeightAtSave);

        textEditorWrapper.style.maxHeight = `${availableHeightAtSave}px`;

        textEditorInput.style.height = `${trimmedHeight}px`;
        textEditorWrapper.style.height = `${trimmedHeight}px`;

        // Clean up: Stop watching for color changes and remove event listeners
        unsubscribe();
        window.removeEventListener('click', handleOutsideClick);
        window.removeEventListener('mousedown', handleOutsideClick);
        textEditorInput.removeEventListener('keydown', handleKeyDown);
        textEditorInput.removeEventListener('beforeinput', handleBeforeInput);
        textEditorInput.removeEventListener('input', handleInput);
        const editorWidth = textEditorWrapper.offsetWidth;
        const editorHeight = textEditorWrapper.offsetHeight;

        // Calculate new position relative to stage before removing wrapper
        const stage = textNode.getStage();
        const currentStageBox = stage?.container().getBoundingClientRect();
        const newX = currentStageBox ? textEditorWrapper.offsetLeft - currentStageBox.left : textNode.x();
        const newY = currentStageBox ? textEditorWrapper.offsetTop - currentStageBox.top : textNode.y();
        // Determine final color: use new color only if actively changed during editing
        const finalBrush = useMagicPenStore.getState().brushColor;
        const finalColor = textColorTracker.wasChanged ? textColorTracker.last : finalBrush;

        // Remove from registry and DOM
        unregisterTextEditor(id);
        textEditorWrapper.parentNode?.removeChild(textEditorWrapper);

        // Update the Konva text node
        textNode.x(newX);
        textNode.y(newY);
        textNode.fill(finalColor);
        if (finalText.trim()) {
          textNode.text(finalText);
        }
        const safeWidth = Math.max(1, editorWidth);
        const safeHeight = Math.max(1, editorHeight);

        textNode.width(safeWidth);
        textNode.height(safeHeight);
        textNode.show();
        setIsEditingText(false);

        const stageWidth = stage?.width() ?? 0;
        const stageHeight = stage?.height() ?? 0;
        const { x: relativeX, y: relativeY } = toRelativePosition(newX, newY, stageWidth, stageHeight);
        const { width: relativeWidth, height: relativeHeight } = toRelativeDimensions(
          safeWidth,
          safeHeight,
          stageWidth,
          stageHeight
        );

        // Update the text object in the store
        if (finalText.trim()) {
          const currentObjects = useMagicPenStore.getState().objects;
          const existingObject = currentObjects.find((obj: ICanvasObject) => obj.id === id);
          if (existingObject) {
            const updated: ICanvasObject = {
              ...existingObject,
              text: finalText,
              width: safeWidth,
              height: safeHeight,
              x: newX,
              y: newY,
              relativeX,
              relativeY,
              relativeWidth,
              relativeHeight,
              fill: finalColor,
              hasSynced: existingObject.hasSynced || !!finalText.trim(),
            };

            const updatedObjects = currentObjects.map((obj: ICanvasObject) => (obj.id === id ? updated : obj));
            setObjects(updatedObjects);

            if (!existingObject.hasSynced) {
              await emitAddAnnotation([{ ...updated, hasSynced: true }]);
            } else {
              await emitUpdateAnnotation([{ ...updated, hasSynced: true }]);
            }
          }
        } else {
          // Remove empty text objects
          const currentObjects = useMagicPenStore.getState().objects;
          const filteredObjects = currentObjects.filter((obj: ICanvasObject) => obj.id !== id);
          setObjects(filteredObjects);
        }

        // Redraw the canvas layer
        setTimeout(() => {
          textNode.getLayer()?.batchDraw();
        }, 10);
      };

      // Auto-resize on Escape key and dynamic height adjustment
      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          removeTextarea();
          return;
        }
        if (isAtBottomLimit) {
          const isModifierKey = e.ctrlKey || e.metaKey || e.altKey;
          // Allow selection shortcuts (Ctrl+A, etc)
          if (isModifierKey && (e.key === 'a' || e.key === 'c' || e.key === 'x')) {
            adjustEditorDimensions();
            return;
          }
        }

        adjustEditorDimensions();
      };
      textEditorInput.addEventListener('keydown', handleKeyDown);
      const handleBeforeInput = (e: InputEvent) => {
        const type = e.inputType;
        const isDeletion = type === 'deleteContentBackward' || type === 'deleteContentForward';
        const isInsertion =
          type.startsWith('insert') ||
          type === 'insertLineBreak' ||
          type === 'insertFromPaste' ||
          type === 'insertFromDrop' ||
          type === 'insertCompositionText';
        if (isDeletion) {
          textEditorInput.style.caretColor = '#000';
          requestAnimationFrame(adjustEditorDimensions);
          return;
        }
        if (!isInsertion) return;

        const start = textEditorInput.selectionStart ?? textEditorInput.value.length;
        const end = textEditorInput.selectionEnd ?? start;
        const originalValue = textEditorInput.value;
        let inserted = '';

        // Safari workaround: don't manually set value in beforeinput if possible
        if (isSafari()) {
          // In Safari, completely bypass the measurement logic in beforeinput.
          // This ensures the browser's input engine is never interrupted.
          return;
        }

        if (type === 'insertLineBreak') {
          inserted = '\\n';
        } else if (typeof (e as any).data === 'string') {
          inserted = (e as any).data as string;
        } else {
          inserted = 'x';
        }

        const candidate = originalValue.slice(0, start) + inserted + originalValue.slice(end);

        const currentScrollHeight0 = textEditorInput.scrollHeight;
        const currentClientHeight = textEditorInput.clientHeight; // excludes borders

        const stageRect = getStageRect();
        const topPx = parsePx(textEditorWrapper.style.top);
        const availableHeight = stageRect ? Math.max(1, stageRect.bottom - topPx) : currentClientHeight;

        // Check if we're already at or near the bottom limit
        const isAlreadyAtBottomLimit = currentScrollHeight0 >= availableHeight - 1; // 1px tolerance

        // For line breaks, prevent if we're already at the bottom limit
        if (type === 'insertLineBreak' && isAlreadyAtBottomLimit) {
          e.preventDefault();
          textEditorInput.style.caretColor = 'red';
          return;
        }

        textEditorInput.value = candidate;
        const candidateScrollHeight = textEditorInput.scrollHeight;

        textEditorInput.value = originalValue;
        try {
          textEditorInput.setSelectionRange(start, end);
        } catch {
          console.error('Text selection failed');
        }

        const increasesHeight = candidateScrollHeight > currentScrollHeight0 + 1; // 1px tolerance
        const fitsCanvas = candidateScrollHeight <= availableHeight - 1; // 1px tolerance
        if (increasesHeight && !fitsCanvas) {
          e.preventDefault();
          textEditorInput.style.caretColor = 'red';
          return;
        }
        textEditorInput.style.caretColor = '#000';
      };
      textEditorInput.addEventListener('beforeinput', handleBeforeInput);

      textEditorInput.addEventListener('input', handleInput);

      // Close editor when clicking outside
      const handleOutsideClick = (e: MouseEvent) => {
        // Check if the click target is within the text editor or its components
        const isClickInsideEditor =
          e.target === textEditorInput || e.target === textEditorWrapper || resizeHandles.some((h) => h === e.target);

        // For touchpad interactions, we need to ensure the focus is properly maintained
        // Check if the active element is still within our editor components
        const isFocusWithinEditor =
          document.activeElement === textEditorInput ||
          document.activeElement === textEditorWrapper ||
          resizeHandles.some((h) => h === document.activeElement);

        // Only close the editor if:
        // 1. The click is outside the editor AND
        // 2. The focus is not within the editor AND
        // 3. The click target is not a child of the editor wrapper
        if (!isClickInsideEditor && !isFocusWithinEditor) {
          // Additional check for touchpad interactions - ensure we're not clicking on a child element
          const isChildOfEditor = textEditorWrapper.contains(e.target as Node);
          if (!isChildOfEditor) {
            removeTextarea();
          }
        }
      };

      // Use a shorter timeout for better touchpad responsiveness
      // Also add both mousedown and click events to handle touchpad interactions properly
      setTimeout(() => {
        window.addEventListener('click', handleOutsideClick);
        window.addEventListener('mousedown', handleOutsideClick);
      }, 50);
    },
    [setIsEditingText, brushColor, setObjects, emitAddAnnotation, emitUpdateAnnotation]
  );

  const handleTextDblClick = useCallback(
    (textNode: Konva.Text, id: string, currentText: string) => {
      createTextEditor(textNode, id, currentText);
    },
    [createTextEditor]
  );

  const handleTextClick = useCallback(
    (e: Konva.KonvaEventObject<MouseEvent | Event>, id: string, currentText: string) => {
      // Stop event propagation to prevent creating new text
      e.cancelBubble = true;
      if (e.evt) {
        e.evt.stopPropagation();
      }

      const textNode = e.target as any;

      // Bring text box to front using the proper store mechanism
      const { bringToFront } = useMagicPenStore.getState();
      bringToFront(id, (updatedObjects) => {
        // Emit update to other peers
        emitUpdateAnnotation(updatedObjects);
      });

      // Open textarea for editing in both text and select mode
      if (activeTool === 'text' || activeTool === 'select') {
        createTextEditor(textNode, id, currentText);
      }
    },
    [createTextEditor, activeTool, emitUpdateAnnotation]
  );

  const createNewText = useCallback(
    async (pos: { x: number; y: number }, stageRef: React.RefObject<Konva.Stage>) => {
      const id = generateUniqueId();
      // Constrain initial position to keep text box fully visible
      const { x: initialX, y: initialY } = constrainInitialPosition(pos, 0, 0, stageRef.current);

      const stage = stageRef.current;
      const containerWidth = stage?.width() ?? 0;
      const containerHeight = stage?.height() ?? 0;
      const { x: relativeX, y: relativeY } = toRelativePosition(initialX, initialY, containerWidth, containerHeight);
      const { width: relativeWidth, height: relativeHeight } = toRelativeDimensions(
        0,
        0,
        containerWidth,
        containerHeight
      );

      const newText: ICanvasObject = {
        id,
        type: EMagicPenShape.TEXT_BOX,
        x: initialX,
        y: initialY,
        text: '',
        fill: brushColor,
        fontSize: 20,
        width: 0,
        height: 0,
        relativeX,
        relativeY,
        relativeWidth,
        relativeHeight,
        opacity: 1,
        createdBy,
        hasSynced: true,
      };
      addObjects([newText]);
      await emitAddAnnotation([newText]);

      // Open text editor immediately after creating the text object
      setTimeout(() => {
        const stageInstance = stageRef.current;
        if (stageInstance) {
          const textNode = stageInstance.findOne(`#${id}`);
          if (textNode) {
            createTextEditor(textNode as Konva.Text, id, '');
          }
        }
      }, 50);
    },
    [brushColor, createdBy, addObjects, createTextEditor, emitAddAnnotation]
  );

  const createSticker = useCallback(
    async (pos: { x: number; y: number }, stageRef: React.RefObject<Konva.Stage>) => {
      if (!activeSticker) return;

      const stage = stageRef.current;
      if (!stage) return;

      const containerWidth = stage.width();
      const containerHeight = stage.height();

      // Calculate responsive font size based on container dimensions
      const responsiveFontSize = calculateResponsiveFontSize(STICKER_FONT_SIZE, containerWidth, containerHeight);

      // Estimate sticker dimensions using responsive font size
      const estimatedWidth = (activeSticker.length || 1) * responsiveFontSize * 0.6;
      const estimatedHeight = responsiveFontSize;

      // Calculate desired position with offset
      const desiredX = pos.x - STICKER_PLACEMENT_OFFSET;
      const desiredY = pos.y - STICKER_PLACEMENT_OFFSET;

      // Constrain initial position to keep sticker fully visible
      const { x: initialX, y: initialY } = constrainInitialPosition(
        { x: desiredX, y: desiredY },
        estimatedWidth,
        estimatedHeight,
        stage
      );

      // Add static top offset to prevent top clipping (5% of font size)
      const topOffset = responsiveFontSize * 0.08;
      const finalY = Math.max(0, initialY + topOffset);

      // Convert to relative coordinates for responsive behavior
      const relativeX = containerWidth > 0 ? initialX / containerWidth : 0;
      const relativeY = containerHeight > 0 ? finalY / containerHeight : 0;

      const stickerObj: ICanvasObject = {
        id: generateUniqueId(),
        type: EMagicPenShape.STICKER,
        createdBy,
        content: activeSticker,
        x: initialX,
        y: finalY,
        width: estimatedWidth,
        height: estimatedHeight,
        relativeX,
        relativeY,
        fontSize: responsiveFontSize,
        scaleX: 1,
        scaleY: 1,
      };

      await handleAddObjects([stickerObj]);
    },
    [activeSticker, createdBy, handleAddObjects]
  );

  return {
    createTextEditor,
    handleTextDblClick,
    handleTextClick,
    createNewText,
    createSticker,
  };
};

export default useTextTool;
